import React, { useState, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  collection,
  getDocs,
  getCountFromServer,
  query,
  where,
  orderBy
} from 'firebase/firestore';
import { LuChartColumn, LuLoaderCircle, LuTriangleAlert } from 'react-icons/lu';
import { db } from '../../config/firebaseConfig';
import GlassIcon from '../../components/ui/GlassIcon';
import Modal from '../../components/ui/Modal';
import SegmentedControl from '../../components/ui/SegmentedControl';
import useAccessText from './accessText';
import { fullName, isActiveEmployee } from './accessUtils';
import UsageOverview from './parts/UsageOverview';
import UsageUsersTable from './parts/UsageUsersTable';

const DAY_MS = 24 * 60 * 60 * 1000;
const RANGE_DAYS = 30;
const CHART_DAYS = 14;

const toMillis = (v) => {
  if (!v) return null;
  if (typeof v.toMillis === 'function') return v.toMillis();
  if (v instanceof Date) return v.getTime();
  if (typeof v.seconds === 'number') return v.seconds * 1000;
  return null;
};

/**
 * Body of the statistics window: reads every included employee's sign-in
 * history once when it opens, then derives the tiles, the 14-day chart, the
 * rankings and the per-user table (the old modal's calculations, unchanged).
 */
const UsageStatsBody = ({ employees, onlineUsers, showPassiveEmployees }) => {
  const { label, locale } = useAccessText();
  const [historyById, setHistoryById] = useState(null); // null = loading
  const [fetchError, setFetchError] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [sortKey, setSortKey] = useState('logins30');
  const [sortDir, setSortDir] = useState('desc');
  const [expandedId, setExpandedId] = useState(null);

  // İstatistikler varsayılan olarak yalnızca aktif çalışanları kapsar;
  // "Pasifleri Göster" açıkken pasifler de dahil edilir.
  const includedEmployees = useMemo(
    () => (showPassiveEmployees ? employees : employees.filter(isActiveEmployee)),
    [employees, showPassiveEmployees]
  );

  // Login geçmişi pencere açılışında bir kez çekilir; canlı durum (çevrimiçi,
  // aktif oturum süresi) her render'da employees prop'undan hesaplanır.
  const employeesRef = useRef(includedEmployees);
  useEffect(() => {
    let cancelled = false;

    const fetchHistory = async () => {
      const since = new Date(Date.now() - RANGE_DAYS * DAY_MS);
      try {
        const entries = await Promise.all(
          employeesRef.current.map(async (emp) => {
            const historyRef = collection(db, 'Employees', emp.id, 'loginHistory');
            const [recentSnap, countSnap] = await Promise.all([
              getDocs(
                query(historyRef, where('timestamp', '>=', since), orderBy('timestamp', 'desc'))
              ),
              getCountFromServer(historyRef)
            ]);
            const recent = recentSnap.docs.map((d) => {
              const data = d.data();
              return { ts: toMillis(data.timestamp), device: data.device || '' };
            });
            const weekAgo = Date.now() - 7 * DAY_MS;
            return [
              emp.id,
              {
                recent,
                logins30: recent.length,
                logins7: recent.filter((r) => r.ts && r.ts >= weekAgo).length,
                totalLogins: countSnap.data().count,
                lastHistoryTs: recent.length > 0 ? recent[0].ts : null
              }
            ];
          })
        );
        if (!cancelled) setHistoryById(Object.fromEntries(entries));
      } catch (error) {
        console.error('Error fetching usage stats:', error);
        if (!cancelled) {
          setFetchError(true);
          setHistoryById({});
        }
      }
    };

    fetchHistory();
    return () => {
      cancelled = true;
    };
  }, []);

  const loading = historyById === null;

  // Çalışan başına birleşik istatistik satırları
  const rows = useMemo(() => {
    if (loading) return [];
    return includedEmployees.map((emp) => {
      const h = historyById[emp.id] || {
        recent: [],
        logins30: 0,
        logins7: 0,
        totalLogins: 0,
        lastHistoryTs: null
      };
      const loginTime = toMillis(emp.loginTime);
      const logoutTime = toMillis(emp.logoutTime);
      const lastLogin = toMillis(emp.lastLogin);
      // sessionStartTime oturum başında bir kez yazılır; loginTime her sayfa
      // geçişinde tazelendiği için oturum süresi hesabında ilki tercih edilir
      const sessionStart = toMillis(emp.sessionStartTime) || loginTime;
      const isOnline = onlineUsers.has(emp.id);

      // Login geçmişine yazılmamış girişleri (ör. geçmişte kamera izni
      // olmadığında kayıt atlanıyordu) oturum alanlarından türet
      const entries = [...h.recent];
      const DEDUP_MS = 45 * 60 * 1000;
      const since30 = Date.now() - RANGE_DAYS * DAY_MS;
      [lastLogin, sessionStart].forEach((ts) => {
        if (!ts || ts < since30) return;
        if (entries.some((e) => e.ts && Math.abs(e.ts - ts) < DEDUP_MS)) return;
        entries.push({ ts, device: '', synthetic: true });
      });
      entries.sort((a, b) => (b.ts || 0) - (a.ts || 0));
      const weekAgo = Date.now() - 7 * DAY_MS;

      // Nabız (lastSeen) en güncel aktivite işareti: sekmeyi kapatarak çıkan
      // kullanıcılarda logoutTime hiç yazılmadığı için tek kaynak odur.
      const lastSeen = toMillis(emp.lastSeen);
      const lastActivity = Math.max(
        loginTime || 0,
        logoutTime || 0,
        lastSeen || 0,
        lastLogin || 0,
        h.lastHistoryTs || 0
      ) || null;

      // Oturumun bittiği an: açık çıkış varsa o, yoksa son nabız.
      const sessionEnd = Math.max(logoutTime || 0, lastSeen || 0) || null;
      const totalOnlineMs = typeof emp.totalOnlineMs === 'number' ? emp.totalOnlineMs : 0;
      const sessionCount = typeof emp.sessionCount === 'number' ? emp.sessionCount : 0;
      return {
        emp,
        ...h,
        recent: entries,
        logins30: entries.length,
        logins7: entries.filter((e) => e.ts && e.ts >= weekAgo).length,
        totalLogins: Math.max(h.totalLogins, entries.length),
        isOnline,
        lastActivity,
        currentSessionMs: isOnline && sessionStart ? Date.now() - sessionStart : null,
        lastSessionMs:
          !isOnline && sessionStart && sessionEnd && sessionEnd >= sessionStart
            ? sessionEnd - sessionStart
            : null,
        totalOnlineMs,
        avgSessionMs: sessionCount > 0 ? totalOnlineMs / sessionCount : null
      };
    });
  }, [includedEmployees, historyById, onlineUsers, loading]);

  // Özet kutuları
  const summary = useMemo(() => {
    const active30 = rows.filter((r) => r.logins30 > 0);
    return {
      onlineCount: rows.filter((r) => r.isOnline).length,
      active30Count: active30.length,
      totalLogins30: rows.reduce((sum, r) => sum + r.logins30, 0),
      totalOnlineMs: rows.reduce((sum, r) => sum + r.totalOnlineMs, 0)
    };
  }, [rows]);

  // Son 14 günün günlük giriş sayıları (grafik için)
  const dailyChart = useMemo(() => {
    const days = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    for (let i = CHART_DAYS - 1; i >= 0; i--) {
      const dayStart = today.getTime() - i * DAY_MS;
      days.push({
        start: dayStart,
        label: new Date(dayStart).toLocaleDateString(locale, { day: '2-digit', month: '2-digit' }),
        weekday: new Date(dayStart).toLocaleDateString(locale, { weekday: 'short' }),
        count: 0
      });
    }
    rows.forEach((r) => {
      r.recent.forEach((entry) => {
        if (!entry.ts) return;
        const day = days.find((d) => entry.ts >= d.start && entry.ts < d.start + DAY_MS);
        if (day) day.count += 1;
      });
    });
    const max = Math.max(1, ...days.map((d) => d.count));
    return { days, max };
  }, [rows, locale]);

  const topActive = useMemo(
    () =>
      [...rows]
        .filter((r) => r.logins30 > 0)
        .sort((a, b) => b.logins30 - a.logins30 || b.totalOnlineMs - a.totalOnlineMs)
        .slice(0, 5),
    [rows]
  );

  const leastActive = useMemo(
    () =>
      [...rows]
        .filter((r) => r.logins30 === 0)
        .sort((a, b) => (a.lastActivity || 0) - (b.lastActivity || 0))
        .slice(0, 5),
    [rows]
  );

  const sortedRows = useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      if (sortKey === 'name') return dir * fullName(a.emp).localeCompare(fullName(b.emp), 'tr');
      const av = a[sortKey] || 0;
      const bv = b[sortKey] || 0;
      return dir * (av - bv);
    });
  }, [rows, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'name' ? 'asc' : 'desc');
    }
  };

  return (
    <div className="tw-space-y-4">
      <SegmentedControl
        layoutId="usage-stats-tabs"
        label={label('stats.tabsLabel')}
        value={activeTab}
        onChange={setActiveTab}
        options={[
          { value: 'overview', label: label('stats.overview') },
          { value: 'users', label: label('stats.users') },
        ]}
      />

      {loading ? (
        <p className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-py-16 tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">
          <LuLoaderCircle aria-hidden="true" className="tw-h-5 tw-w-5 tw-animate-spin tw-text-teal-600" />
          {label('stats.loading')}
        </p>
      ) : (
        <>
          {fetchError && (
            <p role="alert" className="tw-flex tw-items-start tw-gap-2 tw-rounded-2xl tw-border tw-border-amber-400/40 tw-bg-amber-50 tw-px-3.5 tw-py-3 tw-text-sm tw-text-amber-900 dark:tw-border-amber-400/25 dark:tw-bg-amber-400/10 dark:tw-text-amber-100">
              <LuTriangleAlert aria-hidden="true" className="tw-mt-0.5 tw-h-4 tw-w-4 tw-shrink-0" />
              {label('stats.error')}
            </p>
          )}
          {activeTab === 'overview' && (
            <UsageOverview summary={summary} total={rows.length} chart={dailyChart} chartDays={CHART_DAYS} topActive={topActive} leastActive={leastActive} />
          )}
          {activeTab === 'users' && (
            <UsageUsersTable rows={sortedRows} sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} expandedId={expandedId} onExpand={setExpandedId} />
          )}
          <p className="tw-px-1 tw-text-xs tw-leading-relaxed tw-text-slate-500 dark:tw-text-slate-400">{label('stats.footnote')}</p>
        </>
      )}
    </div>
  );
};

UsageStatsBody.propTypes = {
  employees: PropTypes.arrayOf(PropTypes.object).isRequired,
  onlineUsers: PropTypes.instanceOf(Set).isRequired,
  showPassiveEmployees: PropTypes.bool,
};

/** Kullanım İstatistikleri in a dialog; the history is read each time it opens. */
const UsageStatsModal = ({ isOpen, employees, onlineUsers, showPassiveEmployees = false, onClose }) => {
  const { label, language } = useAccessText();
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      title={label('stats.title')}
      subtitle={label('stats.subtitle')}
      closeLabel={label('stats.close')}
      lang={language}
      icon={(
        <GlassIcon gradient="tw-from-sky-400 tw-to-blue-600 tw-shadow-blue-500/30" size="xs">
          <LuChartColumn aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
        </GlassIcon>
      )}
    >
      <UsageStatsBody employees={employees} onlineUsers={onlineUsers} showPassiveEmployees={showPassiveEmployees} />
    </Modal>
  );
};

UsageStatsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  employees: PropTypes.arrayOf(PropTypes.object).isRequired,
  onlineUsers: PropTypes.instanceOf(Set).isRequired,
  showPassiveEmployees: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
};

export default UsageStatsModal;
